import { css } from '@emotion/react';
import humps from 'humps';
import moment from 'moment';
import {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { AngularContext } from 'react-app';
import {
  createCustomDataReport,
  generateCustomDataReport,
  getCustomDataReport,
  getCustomDataReports,
} from 'redux/actions/custom-downloads';
import { getCurrentJourney } from 'redux/selectors/learning-journeys';
import { getCurrentUser } from 'redux/selectors/users';
import {
  JourneyDataDownloadFile,
  JourneyDataDownloadTile,
} from 'redux/schemas/api/custom-downloads';
import { useAppDispatch } from 'redux/store';
import { NvTextInput } from 'shared/components/inputs/nv-text-input';
import NvIcon from 'shared/components/nv-icon';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import PusherService from 'shared/services/pusher-service';
import Spinner from 'shared/components/spinner';

const REPORT_GENERATION_NOT_STARTED = 0;
const REPORT_GENERATION_IN_PROGRESS = 1;
const REPORT_GENERATION_COMPLETED = 2;
const REPORT_GENERATION_FAILED = 3;

type JourneyDataDownloadEvent = {
  courseId: number;
  reportId: number;
  reportType?: string;
};

const DataDownloads = () => {
  const styles = css`
    width: 100%;
    max-width: 960px;
    margin: 0 auto;
    padding: 24px 16px 48px;

    .downloads-header {
      text-align: center;
    }

    .reports-grid {
      display: grid;
      gap: 16px;
      width: 100%;
      margin-top: 32px;
    }

    .report-card {
      width: 100%;
      border: 1px solid #dde3ea;
      border-radius: 12px;
      padding: 24px;
      background: #fff;
      text-align: left;
      transition: box-shadow 0.2s ease, transform 0.2s ease;
      cursor: pointer;
    }

    .report-card:hover {
      transform: translateY(-1px);
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    }

    .report-card-title {
      font-size: 18px;
      font-weight: 600;
    }

    .report-card-status {
      min-height: 52px;
    }

    .report-card-link {
      color: #1b66d2;
      font-weight: 600;
      white-space: nowrap;
    }

    .empty-state {
      width: 100%;
      margin-top: 32px;
      padding: 32px 24px;
      border: 1px dashed #dde3ea;
      border-radius: 12px;
      background: #fafbfc;
      text-align: center;
    }

    .downloads-table th,
    .downloads-table td {
      vertical-align: middle;
    }

    .modal-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 24px;
    }
  `;

  const dispatch = useAppDispatch();
  const currentJourney = useSelector(getCurrentJourney);
  const currentUser = useSelector(getCurrentUser);
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { catalogId } = $state.params;

  const [reports, setReports] = useState<JourneyDataDownloadTile[]>([]);
  const [downloadDetails, setDownloadDetails] = useState<JourneyDataDownloadFile[]>([]);
  const [showCreateDownloadModal, setShowCreateDownloadModal] = useState(false);
  const [showDownloadFilesModal, setShowDownloadFilesModal] = useState(false);
  const [newDownloadName, setNewDownloadName] = useState('');
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [selectedReportTitle, setSelectedReportTitle] = useState('');
  const [isReportsLoading, setIsReportsLoading] = useState(true);
  const [isDownloadDetailsLoading, setIsDownloadDetailsLoading] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [activeGenerateReportId, setActiveGenerateReportId] = useState<number | null>(null);

  const selectedReport = reports.find(({ reportId }) => reportId === selectedReportId) ?? null;
  const currentUserName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ');
  const isSelectedReportGenerating = selectedReport?.reportStatus === REPORT_GENERATION_IN_PROGRESS
    || activeGenerateReportId === selectedReportId;

  const loadReports = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsReportsLoading(true);
    }

    try {
      const result = await dispatch(getCustomDataReports({ catalogId })).unwrap();
      setReports(result.dataDownload ?? []);
    } catch {
      setReports([]);
    } finally {
      if (showLoading) {
        setIsReportsLoading(false);
      }
    }
  }, [catalogId, dispatch]);

  const loadReportDetails = useCallback(async (reportId: number, showLoading = true) => {
    if (showLoading) {
      setIsDownloadDetailsLoading(true);
    }

    try {
      const result = await dispatch(getCustomDataReport({ catalogId, reportId })).unwrap();
      setDownloadDetails(result.downloadDetails ?? []);
    } catch {
      setDownloadDetails([]);
    } finally {
      if (showLoading) {
        setIsDownloadDetailsLoading(false);
      }
    }
  }, [catalogId, dispatch]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    if (!showDownloadFilesModal || !selectedReportId) {
      return;
    }

    loadReportDetails(selectedReportId);
  }, [loadReportDetails, selectedReportId, showDownloadFilesModal]);

  useEffect(() => {
    if (!currentJourney?.id) {
      return undefined;
    }

    const pusherChannel = PusherService.initializeCourseChannel(currentJourney.id);

    if (!pusherChannel) {
      return undefined;
    }

    const reportGenerationHandler = (data) => {
      const pusherData = humps.camelizeKeys(data) as unknown as JourneyDataDownloadEvent;

      if (pusherData.courseId !== currentJourney.id) {
        return;
      }

      if (pusherData.reportType && pusherData.reportType !== 'download') {
        return;
      }

      loadReports(false);

      if (showDownloadFilesModal && selectedReportId === pusherData.reportId) {
        loadReportDetails(selectedReportId, false);
      }
    };

    pusherChannel.bind('custom_report_generated', reportGenerationHandler);
    pusherChannel.bind('custom_report_failed', reportGenerationHandler);

    return () => {
      pusherChannel.unbind('custom_report_generated', reportGenerationHandler);
      pusherChannel.unbind('custom_report_failed', reportGenerationHandler);
    };
  }, [
    currentJourney?.id,
    loadReportDetails,
    loadReports,
    selectedReportId,
    showDownloadFilesModal,
  ]);

  const closeCreateDownloadModal = () => {
    setShowCreateDownloadModal(false);
    setNewDownloadName('');
  };

  const closeDownloadFilesModal = () => {
    setShowDownloadFilesModal(false);
    setSelectedReportId(null);
    setSelectedReportTitle('');
    setDownloadDetails([]);
    setActiveGenerateReportId(null);
  };

  const handleCreateNewDataDownload = async () => {
    setIsCreatingReport(true);

    try {
      await dispatch(createCustomDataReport({
        catalogId,
        title: newDownloadName || 'Untitled Report',
      })).unwrap();

      await loadReports(false);
      closeCreateDownloadModal();
    } catch {
      return;
    } finally {
      setIsCreatingReport(false);
    }
  };

  const handleOpenDownloadFilesModal = (report: JourneyDataDownloadTile) => {
    setSelectedReportId(report.reportId);
    setSelectedReportTitle(report.reportTitle);
    setDownloadDetails([]);
    setShowDownloadFilesModal(true);
  };

  const handleGenerateLatestVersion = async () => {
    if (!selectedReportId) {
      return;
    }

    setActiveGenerateReportId(selectedReportId);

    try {
      await dispatch(generateCustomDataReport({
        catalogId,
        reportId: selectedReportId,
      })).unwrap();

      await loadReports(false);
      await loadReportDetails(selectedReportId, false);
    } catch {
      return;
    } finally {
      setActiveGenerateReportId(null);
    }
  };

  const formatDate = (date?: string | null, format = 'MOMENT.MONTH_DAY_YEAR_TIME') => (
    date ? moment(date).format(format) : ''
  );

  const getReportGeneratedAt = (report: JourneyDataDownloadTile) => (
    report.generatedAt || report.reportTimestamp || report.createdAt
  );

  const renderReportStatus = (report: JourneyDataDownloadTile) => {
    if (report.reportStatus === REPORT_GENERATION_IN_PROGRESS) {
      return (
        <div>
          <div className='d-flex align-items-center'>
            <Spinner size={16} />
            <span className='ml-2'>{t.CUSTOM_DOWNLOADS.PREPARING_DOWNLOAD()}</span>
          </div>
          <div className='text-small color-gray-2 mt-2'>
            {t.CUSTOM_DOWNLOADS.RECEIVE_EMAIL_WHEN_READY()}
          </div>
        </div>
      );
    }

    if (report.reportStatus === REPORT_GENERATION_COMPLETED) {
      return (
        <div className='text-small color-gray-2'>
          {t.CUSTOM_DOWNLOADS.GENERATED_ON(formatDate(getReportGeneratedAt(report)))}
        </div>
      );
    }

    if (report.reportStatus === REPORT_GENERATION_FAILED) {
      return (
        <div className='text-small text-danger'>
          {t.CUSTOM_DOWNLOADS.FAILED_TO_GENERATE()}
        </div>
      );
    }

    if (report.reportStatus === REPORT_GENERATION_NOT_STARTED) {
      return (
        <div className='text-small color-gray-2'>
          {t.CUSTOM_DOWNLOADS.NO_DOWNLOAD_GENERATED()}
        </div>
      );
    }

    return null;
  };

  return (
    <div css={styles} className='d-flex flex-column align-items-center'>
      <div className='downloads-header'>
        <div className='title heading-2 mt-2'>
          {t.LEARNING_JOURNEYS.DATA_DOWNLOADS.TITLE()}
        </div>
        <div className='text-body color-gray-2 mb-6'>
          {t.LEARNING_JOURNEYS.DATA_DOWNLOADS.DESCRIPTION()}
        </div>
        <Button
          variant='link'
          className='d-inline-flex align-items-center'
          onClick={() => setShowCreateDownloadModal(true)}
        >
          <NvIcon icon='add' size='xss-smallest' className='mr-1' />
          {t.LEARNING_JOURNEYS.DATA_DOWNLOADS.CREATE_NEW()}
        </Button>
      </div>

      {isReportsLoading && (
        <div className='empty-state'>
          <div className='d-flex justify-content-center'>
            <Spinner size={24} />
          </div>
        </div>
      )}

      {!isReportsLoading && reports.length > 0 && (
        <div className='reports-grid'>
          {reports.map((report) => (
            <button
              key={report.reportId}
              type='button'
              className='report-card'
              onClick={() => handleOpenDownloadFilesModal(report)}
            >
              <div className='d-flex justify-content-between align-items-start'>
                <div className='report-card-title'>{report.reportTitle}</div>
                <div className='report-card-link text-small'>
                  {t.CUSTOM_DOWNLOADS.VIEW_DOWNLOADS()}
                </div>
              </div>
              <div className='report-card-status mt-4'>
                {renderReportStatus(report)}
              </div>
            </button>
          ))}
        </div>
      )}

      {!isReportsLoading && reports.length === 0 && (
        <div className='empty-state text-body color-gray-2'>
          {t.CUSTOM_DOWNLOADS.NO_DOWNLOAD_GENERATED()}
        </div>
      )}

      {showCreateDownloadModal && (
        <NvModal
          type={ModalType.FIXED}
          show={showCreateDownloadModal}
          height={400}
          width={560}
          header={t.LEARNING_JOURNEYS.DATA_DOWNLOADS.CREATE_NEW_DOWNLOAD_MODAL.TITLE()}
          onClose={closeCreateDownloadModal}
          body={(
            <div className='d-flex flex-column align-items-center'>
              <div>{t.LEARNING_JOURNEYS.DATA_DOWNLOADS.CREATE_NEW_DOWNLOAD_MODAL.NAME_YOUR_DOWNLOAD()}</div>
              <NvTextInput
                value={newDownloadName}
                placeholder={t.LEARNING_JOURNEYS.DATA_DOWNLOADS.CREATE_NEW_DOWNLOAD_MODAL.UNTITLED()}
                onChange={(event) => setNewDownloadName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleCreateNewDataDownload();
                  }
                }}
              />
              <div className='d-flex mt-4'>
                <Button
                  variant='secondary'
                  className='mr-2'
                  onClick={closeCreateDownloadModal}
                  disabled={isCreatingReport}
                >
                  {t.FORM.CANCEL()}
                </Button>
                <Button
                  variant='primary'
                  onClick={handleCreateNewDataDownload}
                  disabled={isCreatingReport}
                >
                  {t.FORM.CREATE()}
                </Button>
              </div>
            </div>
          )}
        />
      )}

      {showDownloadFilesModal && (
        <NvModal
          type={ModalType.FIXED}
          show={showDownloadFilesModal}
          height={560}
          width={760}
          header={selectedReport?.reportTitle || selectedReportTitle}
          onClose={closeDownloadFilesModal}
          body={(
            <div>
              {isDownloadDetailsLoading ? (
                <div className='d-flex justify-content-center py-6'>
                  <Spinner size={24} />
                </div>
              ) : (
                <>
                  <div className='table-responsive'>
                    <table className='table downloads-table mb-0'>
                      <thead>
                        <tr>
                          <th>{t.CUSTOM_DOWNLOADS.GENERATED_AT()}</th>
                          <th>{t.CUSTOM_DOWNLOADS.BY()}</th>
                          <th>{t.FILE_UPLOAD.DOWNLOAD()}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isSelectedReportGenerating && (
                          <tr>
                            <td>
                              <span className='color-gray-2'>
                                {t.CUSTOM_DOWNLOADS.PREPARING_DOWNLOAD()}
                              </span>
                            </td>
                            <td>{currentUserName || '-'}</td>
                            <td>
                              <Spinner size={16} />
                            </td>
                          </tr>
                        )}
                        {downloadDetails.map((file) => (
                          <tr key={file.fileId}>
                            <td>
                              {file.reportFileStatus === REPORT_GENERATION_FAILED && (
                                <span className='text-danger font-weight-bold'>
                                  {t.CUSTOM_DOWNLOADS.FAILED()}
                                </span>
                              )}
                              {file.reportFileStatus !== REPORT_GENERATION_FAILED && file.finishedAt && (
                                <span>
                                  {formatDate(file.finishedAt, 'MOMENT.MONTH_DAY_COMMA_YEAR_AT_TIME')}
                                </span>
                              )}
                              {file.reportFileStatus !== REPORT_GENERATION_FAILED && !file.finishedAt && (
                                <span className='color-gray-2'>
                                  {t.CUSTOM_DOWNLOADS.PREPARING_DOWNLOAD()}
                                </span>
                              )}
                            </td>
                            <td>{file.userName || '-'}</td>
                            <td>
                              {file.reportFileStatus === REPORT_GENERATION_COMPLETED && file.url && (
                                <a
                                  href={file.url}
                                  target='_blank'
                                  rel='noreferrer'
                                  download
                                >
                                  {t.FILE_UPLOAD.DOWNLOAD()}
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                        {!isSelectedReportGenerating && downloadDetails.length === 0 && (
                          <tr>
                            <td colSpan={3} className='text-center color-gray-2'>
                              {t.CUSTOM_DOWNLOADS.NO_DOWNLOAD_GENERATED()}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className='modal-actions'>
                    <Button
                      variant='primary'
                      onClick={handleGenerateLatestVersion}
                      disabled={!selectedReportId || isSelectedReportGenerating}
                    >
                      {isSelectedReportGenerating
                        ? t.CUSTOM_DOWNLOADS.GENERATING()
                        : t.CUSTOM_DOWNLOADS.GENERATE_LATEST_VERSION()}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        />
      )}
    </div>
  );
};

export default DataDownloads;
